<script setup lang="ts">
import { computed } from 'vue';
import RatingEmoji from './RatingEmoji.vue';
import type { RatingValue } from './RatingEmoji.types';
import { createTextStyle } from '@/common/customisation/utils/styleUtils';
import {
  useCsatRatingOptionsHeadingCustomisation,
  useCsatRatingOptionCustomisation,
  useCsatRatingOptionsStylesCustomisation,
} from '../composables/useCsatRatingOptionsCustomisation';
import type { RatingOptionData } from '../types/csat.types';
import { useRatingDisplay } from '../config/ratingDisplay';

interface Props {
  selectedRating: RatingValue | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'rating-selected', value: RatingValue): void;
}>();

const {
  textContent: questionText,
  textColor: questionTextColor,
  isVisible: isRatingHeadingVisible,
} = useCsatRatingOptionsHeadingCustomisation();

const { labelColor: ratingLabelColor, selectedLabelColor: ratingSelectedLabelColor } =
  useCsatRatingOptionsStylesCustomisation();

const { config: ratingDisplayConfig } = useRatingDisplay();

/** Whether a given rating option should render as selected under the active display mode. */
const isOptionSelected = (value: RatingValue) => {
  if (props.selectedRating === null) return false;
  return ratingDisplayConfig.value.cumulativeSelection ? props.selectedRating >= value : props.selectedRating === value;
};

const RATING_VALUES: RatingValue[] = [1, 2, 3, 4, 5];

const ratingOptionsConfig = RATING_VALUES.map((value) => useCsatRatingOptionCustomisation(value));

const ratingOptions = computed<RatingOptionData[]>(() => {
  return ratingOptionsConfig
    .map((config, index) => ({
      value: RATING_VALUES[index],
      label: config.labelText.value,
      isVisible: config.isVisible.value,
    }))
    .filter((option) => option.isVisible);
});

const questionStyle = computed(() => createTextStyle(questionTextColor));

const handleRatingClick = (value: RatingValue) => {
  emit('rating-selected', value);
};
</script>

<template>
  <div class="csat-rating-section">
    <div v-if="isRatingHeadingVisible" class="csat-rating-section__question theme-font-h4" :style="questionStyle">
      {{ questionText }}
    </div>

    <div class="csat-rating-section__ratings">
      <RatingEmoji
        v-for="option in ratingOptions"
        :key="option.value"
        :value="option.value"
        :label="option.label"
        :label-color="ratingLabelColor"
        :selected-label-color="ratingSelectedLabelColor"
        :is-selected="isOptionSelected(option.value)"
        :image-src="ratingDisplayConfig.resolveImageSrc(option.value, isOptionSelected(option.value))"
        :animate="ratingDisplayConfig.animateSelection"
        @click="handleRatingClick"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/mixin.scss';

.csat-rating-section {
  &__question {
    margin-bottom: 24px;
    text-align: center;
  }

  &__ratings {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 32px;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;

    @include respond(phone) {
      gap: 2px;
      margin-bottom: 24px;
    }
  }
}
</style>
